import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  GazePoint,
  DifferentialColorScheme,
  DifferentialNormalizationMethod,
} from "../../types/gazeHeatmapTypes";
import { useDifferentialHeatmap } from "../../hooks/useDifferentialHeatmap";
import EquirectangularImage from "./EquirectangularImage";
import GazeHeatmapCanvas from "./GazeHeatmapCanvas";
import DifferentialHeatmapLegend from "./DifferentialHeatmapLegend";

interface DifferentialHeatmapViewProps {
  /** Scene media URL for background */
  mediaUrl: string;
  /** Scene name for alt text */
  sceneName: string;
  /** Scene rotation for coordinate conversion */
  sceneRotationY?: number;
  /** Group A data */
  groupA: {
    name: string;
    color?: string;
    points: GazePoint[];
  };
  /** Group B data */
  groupB: {
    name: string;
    color?: string;
    points: GazePoint[];
  };
  /** Heatmap opacity (0-1) */
  opacity?: number;
  /** Color scheme */
  colorScheme?: DifferentialColorScheme;
  /** Normalization method */
  normalization?: DifferentialNormalizationMethod;
  /** Additional CSS classes */
  className?: string;
}

/**
 * View component for displaying a differential heatmap comparison
 * Shows where Group A looks more (red) vs Group B looks more (blue)
 */
const DifferentialHeatmapView: React.FC<DifferentialHeatmapViewProps> = ({
  mediaUrl,
  sceneName,
  sceneRotationY = 0,
  groupA,
  groupB,
  opacity = 0.7,
  colorScheme = "redBlue",
  normalization = "perSample",
  className = "",
}) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);

  // Configuration
  const config = useMemo(
    () => ({
      colorScheme,
      normalization,
    }),
    [colorScheme, normalization],
  );

  // Generate differential heatmap
  const { imageData, result, isGenerating, error } = useDifferentialHeatmap({
    pointsA: groupA.points,
    pointsB: groupB.points,
    config,
    enabled: imageLoaded && groupA.points.length > 0 && groupB.points.length > 0,
    sceneRotationY,
  });

  // Handle no data case
  if (groupA.points.length === 0 || groupB.points.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center h-64 gap-4 ${className}`}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
        <p className="text-gray-500">{t("statsNew.differential.needBothGroups")}</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Viewer */}
      <div className="relative aspect-[2/1] bg-gray-100 rounded-lg overflow-hidden">
        {/* Background image */}
        <EquirectangularImage src={mediaUrl} alt={sceneName} onLoad={() => setImageLoaded(true)} />

        {/* Differential heatmap overlay */}
        <GazeHeatmapCanvas imageData={imageData} opacity={opacity} />

        {/* Generation indicator */}
        {isGenerating && (
          <div className="absolute top-4 right-4 bg-white rounded-lg shadow px-3 py-2 flex items-center gap-2 z-10">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
            <span className="text-sm text-gray-600">{t("statsNew.differential.generating")}</span>
          </div>
        )}

        {/* Error indicator */}
        {error && (
          <div className="absolute top-4 right-4 bg-red-50 text-red-600 rounded-lg shadow px-3 py-2 text-sm z-10">
            {error}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4">
        <DifferentialHeatmapLegend
          colorScheme={colorScheme}
          groupAName={groupA.name}
          groupBName={groupB.name}
          result={result}
        />
      </div>
    </div>
  );
};

export default DifferentialHeatmapView;
